import useLibraryGroups from '@/pages/Library/hooks/useLibraryGroups.ts'
import useLibraryDocuments from '@/pages/Library/hooks/useLibraryDocuments.ts'
import MainLayout from '@/shared/Layouts/MainLayout'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import css from './library.module.css'
import useTreeSelectCtrl from '@/shared/Tree/ctrl/useTreeSelectCtrl.ts'
import { IParameterGroup } from '@/pages/Parameters/parameters.types.ts'
import ResizableByDrag from '@/shared/Common/ResizableByDrag'
import LibraryGroupsTree from '@/pages/Library/components/LibraryGroupsTree'
import { useMemo } from 'react'
import breadcrumbPathsUtils from '@/shared/BreadcrumbPaths/breadcrumbPaths.utils.ts'
import BreadcrumbPaths from '@/shared/BreadcrumbPaths'
import SeparateLine from '@/shared/Forms/SeparateLine'
import PlusIcon from '@/assets/icons/plus.svg?react'
import domUtils from '@/utils/dom.utils.ts'
import useHttpLoader from '@/shared/hooks/useHttpLoader.ts'
import libraryDocumentsApi from '@/pages/Library/libraryDocuments.api.ts'
import libraryEvents from '@/pages/Library/events/library.events.ts'
import documentUrlService from '@/pages/Library/services/documentUrl.service.ts'
import { ILibraryDocument } from '@/pages/Library/libraryDocuments.types.ts'
import mediaPreviewService from '@/services/MediaPreviewService/mediaPreview.service.ts'
import DropArea from '@/shared/Files/Droparea'
import useDroparea from '@/shared/Files/hooks/useDroparea.ts'
import useAccessToken from '@/pages/Auth/hooks/useAccessToken.ts'
import LibraryDocumentTile from '@/pages/Library/components/LibraryDocumentTile'
import confirmService from '@/services/ConfirmService/confirm.service'
import useTitle from '@/shared/hooks/useTitle'

const Library = () => {
  useTitle('Библиотека документов')
  const accessToken = useAccessToken()
  const { wait, loading } = useHttpLoader()

  const { libraryGroupsState, loading: groupsLoading } = useLibraryGroups()
  const { libraryDocumentsState, loading: documentsLoading } = useLibraryDocuments()
  const treeSelectCtrl = useTreeSelectCtrl<IParameterGroup['id']>('library-groups-tree', null)

  const group = useMemo(() => {
    return libraryGroupsState.items.find((g) => g.id === treeSelectCtrl.selectedItem)
  }, [libraryGroupsState.items, treeSelectCtrl.selectedItem])

  const selectedGroupBreadcrumbsPaths = useMemo(() => {
    if (!group) return []

    return breadcrumbPathsUtils.getPathsGroup(group.id, libraryGroupsState.items, (group) => group.name)
  }, [libraryGroupsState.items, group])

  const filteredDocuments: ILibraryDocument[] = useMemo(() => {
    if (!group) return []

    return libraryDocumentsState.items.filter((d) => d.groupId === group.id)
  }, [group?.id, libraryDocumentsState.items])

  const openMediaPreview = (index: number) => {
    mediaPreviewService.requestMediaPreview(
      filteredDocuments.map((f) => ({
        ...f,
        url: documentUrlService.generateDocUrl(f, accessToken),
      })),
      index
    )
  }

  const handleUploadFile = () => {
    domUtils
      .requestUploadFile()
      .then((file) => {
        wait(libraryDocumentsApi.createLibraryDocument(group.id, file), (resp) => {
          if (resp.status === 'success') {
            libraryEvents.createdDocument(resp.body)
          }
        })
      })
      .catch()
  }

  const dropAreaCtrl = useDroparea({
    onFiles: (files) => {
      const job = async () => {
        const resp = await Promise.all(files.map((f) => libraryDocumentsApi.createLibraryDocument(group.id, f)))

        return resp
      }

      wait(job(), (resps) => {
        for (const resp of resps) {
          if (resp.status === 'success') {
            libraryEvents.createdDocument(resp.body)
          }
        }
      })
    },
  })

  const handleDelete = (documentId: ILibraryDocument['id']) => {
    confirmService.requestConfirm({
      buttonType: 'primary',
      description: 'Вы действительно хотите удалить файл?',
      onConfirm: () =>
        libraryDocumentsApi.deleteLibraryDocument(documentId).then((r) => {
          if (r.status === 'success') {
            libraryEvents.deletedDocument(r.body.id)
          }
        }),
    })
  }

  const renderContent = () => {
    if (groupsLoading || documentsLoading) {
      return (
        <div className="flex-lines width100 center">
          <ThreeDotsLoader inscription="Загрузка изображений..." />
        </div>
      )
    }

    return (
      <div className={css.wrapper}>
        <div className={css.mainCard}>
          <div className="inline-flex-gap gap24 height100">
            <ResizableByDrag initialWidth={280} resizerClassname={css.resizer} wrapperClassname={css.resizerWrapper}>
              <LibraryGroupsTree treeSelectCtrl={treeSelectCtrl as any} />
            </ResizableByDrag>
            <div className="flex-lines width100">
              <div className="flex-lines gap8 width100">
                <div className="flex-lines gap16">
                  <BreadcrumbPaths
                    paths={selectedGroupBreadcrumbsPaths}
                    styleLastPath={{ color: 'var(--text-description)' }}
                  />
                  <h3 className="text-heading-3">{group?.name}</h3>
                </div>
                <SeparateLine />
              </div>
              <DropArea
                onDrop={dropAreaCtrl.handleDrop}
                isActive={dropAreaCtrl.over}
                onDragOver={dropAreaCtrl.handleDragOver}
                onDragEnter={dropAreaCtrl.handleDragEnter}
                onDragLeave={dropAreaCtrl.handleDragLeave}
                style={{ flex: 1, minHeight: 0, overflow: 'auto', paddingTop: '24px' }}
              >
                <div className="inline-flex-gap gap24" style={{ flexWrap: 'wrap' }}>
                  <div className={css.createTileWrapper} onClick={handleUploadFile}>
                    {loading ? (
                      <ThreeDotsLoader inscription={`Загрузка файлов\nНе уходите со страницы`} />
                    ) : (
                      <>
                        <div className={css.plusIconWrapper}>
                          <PlusIcon className={css.plusIcon} />
                        </div>
                        <p className="text-nm text-secondary text-center" style={{ width: '80%' }}>
                          Нажмите либо перетащите файлы для загрузки
                        </p>
                      </>
                    )}
                  </div>
                  {filteredDocuments.map((d, index) => (
                    <LibraryDocumentTile
                      key={d.id}
                      onClick={() => openMediaPreview(index)}
                      onDelete={() => handleDelete(d.id)}
                      documentUrl={documentUrlService.generateDocUrl(d, accessToken)}
                      document={d}
                      index={index}
                    />
                  ))}
                </div>
              </DropArea>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return <MainLayout>{renderContent()}</MainLayout>
}

export default Library
